import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ChartBox } from "./ReportViz";
import { TrendDataPoint } from "../hooks/useSocialMediaReport";

interface ConversionTrendChartProps {
  historicalData: TrendDataPoint[];
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const LINES = [
  { key: "Link taps", color: "#EC4899", dataKey: "profileLinksTaps" },
  { key: "Website clicks", color: "#3B82F6", dataKey: "websiteClicks" },
] as const;

// Paid stays visually apart from the organic lines (DD-021): the
// organic trend is what the agency optimises and claims credit for.
const PAID_LINE = {
  key: "Paid link clicks",
  color: "#9CA3AF",
  dataKey: "paidLinkClicks",
} as const;

/** Line chart for IG conversion signals across the trend window. These
 *  are the metrics closest to actual booking intent, so the agency
 *  optimises for them. Paid link clicks render as a separate dashed
 *  line; months without ad data are gaps, never zeros. */
export const ConversionTrendChart: React.FC<ConversionTrendChartProps> = ({
  historicalData,
}) => {
  const chartData = historicalData.map((d) => ({
    month: MONTH_NAMES[d.month - 1],
    profileLinksTaps: d.instagramConversion?.profileLinksTaps ?? 0,
    websiteClicks: d.instagramConversion?.websiteClicks ?? 0,
    paidLinkClicks: d.paidConversion ? d.paidConversion.linkClicks : null,
  }));

  const hasAnyData = chartData.some(
    (d) => d.profileLinksTaps + d.websiteClicks > 0,
  );
  const hasPaidData = chartData.some((d) => d.paidLinkClicks !== null);

  if (!hasAnyData) return null;

  const first = chartData[0];
  const last = chartData[chartData.length - 1];
  const tapsDelta = last && first ? last.profileLinksTaps - first.profileLinksTaps : 0;
  const tapsDirection =
    tapsDelta > 0 ? "up" : tapsDelta < 0 ? "down" : "flat";

  const directionLabel = {
    up: { text: "Trending up", color: "text-green-600", arrow: "↑" },
    down: { text: "Trending down", color: "text-red-600", arrow: "↓" },
    flat: { text: "Flat", color: "text-gray-500", arrow: "→" },
  }[tapsDirection];

  return (
    <div>
      {/* Direction chip — title/subtitle live in the ReportPanel header. */}
      <div className="flex justify-end mb-2">
        <span className={`text-xs font-medium ${directionLabel.color}`}>
          {directionLabel.arrow} {directionLabel.text}
        </span>
      </div>
      <ChartBox height={220}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
            <Tooltip />
            {LINES.map((l) => (
              <Line
                key={l.key}
                type="monotone"
                dataKey={l.dataKey}
                name={l.key}
                stroke={l.color}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
            {hasPaidData && (
              <Line
                key={PAID_LINE.key}
                type="monotone"
                dataKey={PAID_LINE.dataKey}
                name={PAID_LINE.key}
                stroke={PAID_LINE.color}
                strokeWidth={2}
                strokeDasharray="5 4"
                connectNulls={false}
                dot={{ r: 3, fill: PAID_LINE.color }}
                activeDot={{ r: 5 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </ChartBox>
      <div className="flex items-center justify-center gap-4 mt-1">
        {LINES.map((l) => (
          <div key={l.key} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
            <span className="text-xs text-gray-500">{l.key}</span>
          </div>
        ))}
        {hasPaidData && (
          <div className="flex items-center gap-1.5">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: PAID_LINE.color }}
            />
            <span className="text-xs text-gray-500">{PAID_LINE.key}</span>
          </div>
        )}
      </div>
    </div>
  );
};
